import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    Button,
    Grid,
    useTheme,
    useMediaQuery,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Badge,
    Paper
} from "@mui/material";
import {
    AccessTime,
    VideoCall,
    Groups,
    Description,
    Assignment,
    School,
    Person,
    Event,
    ExpandMore,
    Edit,
    Delete,
    Link,
    CalendarToday,
    PeopleOutline
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useAppSelector, useAppDispatch } from "../../../../Redux/Store/Hooks";
import { getScheduledClasses } from "../../../../Redux/Slices/ScheduledClasses/ScheduledClassesSlice";
import { useNavigate } from "react-router-dom";

interface ScheduledCalendarListProps {
    subjectId?: number;
    questionTypeId?: number;
    tutorId?: number;
    limit?: number;
    showPastClasses?: boolean;
    showUpcomingOnly?: boolean;
    compact?: boolean;
}

const ScheduledCalendarList: React.FC<ScheduledCalendarListProps> = ({
    subjectId,
    questionTypeId,
    tutorId,
    limit = 50,
    showPastClasses = false,
    showUpcomingOnly = false,
    compact = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { data: scheduledClassesData, isLoading } = useAppSelector((state) => state.ScheduledClassesSlice || { data: null });
    const [expandedClass, setExpandedClass] = useState<number | false>(false);

    useEffect(() => {
        const filters: any = {
            limit,
            ...(subjectId && { subject_id: subjectId }),
            ...(questionTypeId && { question_type_id: questionTypeId }),
            ...(tutorId && { tutor_id: tutorId })
        };

        dispatch(getScheduledClasses(filters));
    }, [dispatch, subjectId, questionTypeId, tutorId, limit]);

    const formatClassData = (rawData: any): any[] => {
        if (!rawData) return [];
        
        if (Array.isArray(rawData)) {
            return rawData.map((item: any) => ({
                id: item.id,
                title: item.class_title || "Scheduled Class",
                fromTime: item.from_time,
                toTime: item.to_time,
                meetingLink: item.meeting_link,
                description: item.description,
                status: item.status,
                calendarEventId: item.calendar_event_id,
                courseId: item.course_id,
                courseFullName: item.course_full_name,
                courseShortName: item.course_short_name,
                createdAt: item.created_at,
                updatedAt: item.updated_at,
                allowScreenSharing: item.allow_screen_sharing,
                autoRecording: item.auto_recording,
                allowMicrophone: item.allow_microphone,
                allowPublicChat: item.allow_public_chat,
                allowScreenShare: item.allow_screen_share,
                tutor: item.tutor,
                subject: item.subject,
                questionType: item.question_type,
                participants: item.participants || [],
                totalParticipants: item.total_participants,
                participantsJoined: item.participants_joined,
                participantsLeft: item.participants_left,
                worksheets: item.worksheets || [],
                sampleBooklets: item.sample_booklets || [],
                booklets: item.booklets || [],
                videoLessons: item.video_lessons || [],
                formulaNotes: item.formula_notes || [],
                peopleData: item.people_data || {}
            }));
        } else if (rawData.data && Array.isArray(rawData.data)) {
            return rawData.data.map((item: any) => ({
                id: item.id,
                title: item.class_title || "Scheduled Class",
                fromTime: item.from_time,
                toTime: item.to_time,
                meetingLink: item.meeting_link,
                description: item.description,
                status: item.status,
                calendarEventId: item.calendar_event_id,
                courseId: item.course_id,
                courseFullName: item.course_full_name,
                courseShortName: item.course_short_name,
                createdAt: item.created_at,
                updatedAt: item.updated_at,
                allowScreenSharing: item.allow_screen_sharing,
                autoRecording: item.auto_recording,
                allowMicrophone: item.allow_microphone,
                allowPublicChat: item.allow_public_chat,
                allowScreenShare: item.allow_screen_share,
                tutor: item.tutor,
                subject: item.subject,
                questionType: item.question_type,
                participants: item.participants || [],
                totalParticipants: item.total_participants,
                participantsJoined: item.participants_joined,
                participantsLeft: item.participants_left,
                worksheets: item.worksheets || [],
                sampleBooklets: item.sample_booklets || [],
                booklets: item.booklets || [],
                videoLessons: item.video_lessons || [],
                formulaNotes: item.formula_notes || [],
                peopleData: item.people_data || {}
            }));
        }
        return [];
    };

    const classes = formatClassData(scheduledClassesData);

    const filteredClasses = classes.filter(cls => {
        const classDateTime = dayjs(cls.fromTime);
        const now = dayjs();
        
        if (showUpcomingOnly) {
            return classDateTime.isAfter(now);
        }
        
        if (!showPastClasses) {
            return classDateTime.isAfter(now) || classDateTime.isSame(now, 'day');
        }
        
        return true;
    }).sort((a: any, b: any) => {
        const dateA = dayjs(a.fromTime);
        const dateB = dayjs(b.fromTime);
        return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
    });

    const handleJoinClass = (meetingLink: string, title: string) => {
        window.open(meetingLink, '_blank');
    };

    const handleEditClass = (classId: number) => {
        navigate(`/online-tutoring/schedule?edit=${classId}`);
    };

    const isClassLive = (fromTime: string, toTime: string) => {
        const now = dayjs();
        const start = dayjs(fromTime);
        const end = dayjs(toTime);
        return now.isAfter(start) && now.isBefore(end);
    };

    const isClassUpcoming = (fromTime: string) => {
        const now = dayjs();
        const start = dayjs(fromTime);
        return start.isAfter(now);
    };

    const getStatusColor = (fromTime: string, toTime: string) => {
        if (isClassLive(fromTime, toTime)) return "success";
        if (isClassUpcoming(fromTime)) return "primary";
        return "default";
    };

    const getStatusText = (fromTime: string, toTime: string) => {
        if (isClassLive(fromTime, toTime)) return "Live Now";
        if (isClassUpcoming(fromTime)) return "Upcoming";
        return "Ended";
    };

    const formatDuration = (fromTime: string, toTime: string) => {
        const start = dayjs(fromTime);
        const end = dayjs(toTime);
        const duration = end.diff(start, 'minute');
        const hours = Math.floor(duration / 60);
        const minutes = duration % 60;
        
        if (hours > 0) {
            return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
        }
        return `${minutes}m`;
    };

    if (isLoading) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography>Loading scheduled classes...</Typography>
            </Box>
        );
    }

    if (filteredClasses.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <CalendarToday sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    No scheduled classes found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {showUpcomingOnly ? "No upcoming classes scheduled" : "No classes match the current filters"}
                </Typography>
            </Box>
        );
    }

    if (compact) {
        return (
            <List dense>
                {filteredClasses.map((cls: any) => (
                    <ListItem key={cls.id} divider>
                        <ListItemAvatar>
                            <Badge badgeContent={getStatusText(cls.fromTime, cls.toTime)} color={getStatusColor(cls.fromTime, cls.toTime) as any}>
                                <Avatar sx={{ bgcolor: '#1a73e8' }}>
                                    <School />
                                </Avatar>
                            </Badge>
                        </ListItemAvatar>
                        <ListItemText
                            primary={cls.title}
                            secondary={
                                <Box>
                                    <Typography variant="caption" display="block">
                                        {dayjs(cls.fromTime).format('MMM D, YYYY - h:mm A')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {cls.tutor?.username} • {cls.subject?.name}
                                    </Typography>
                                </Box>
                            }
                        />
                        <ListItemSecondaryAction>
                            {isClassLive(cls.fromTime, cls.toTime) && cls.meetingLink && (
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<VideoCall />}
                                    onClick={() => handleJoinClass(cls.meetingLink, cls.title)}
                                >
                                    Join
                                </Button>
                            )}
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
        );
    }

    return (
        <Box sx={{ p: isMobile ? 1 : 2 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: '#3c4043' }}>
                Scheduled Classes
            </Typography>

            <Grid container spacing={2}>
                {filteredClasses.map((cls: any) => (
                    <Grid item xs={12} sm={12} md={6} lg={4} key={cls.id}>
                        <Card 
                            sx={{ 
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    boxShadow: theme.shadows[8],
                                    transform: 'translateY(-2px)'
                                }
                            }}
                        >
                            <Box sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1
                            }}>
                                <Chip
                                    label={getStatusText(cls.fromTime, cls.toTime)}
                                    color={getStatusColor(cls.fromTime, cls.toTime) as any}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>

                            <CardContent sx={{ flex: 1, pb: 2 }}>
                                <Box sx={{ mb: 2, pr: 8 }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 600, 
                                        mb: 1,
                                        lineHeight: 1.2,
                                        color: '#3c4043'
                                    }}>
                                        {cls.title}
                                    </Typography>
                                    
                                    {cls.courseFullName && (
                                        <Typography variant="caption" color="primary" sx={{ fontWeight: 500 }}>
                                            {cls.courseFullName}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <AccessTime sx={{ fontSize: 16, mr: 1, color: '#5f6368' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {dayjs(cls.fromTime).format('MMM D, YYYY')}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <AccessTime sx={{ fontSize: 16, mr: 1, color: '#5f6368' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {dayjs(cls.fromTime).format('h:mm A')} - {dayjs(cls.toTime).format('h:mm A')}
                                        <span style={{ marginLeft: '8px', color: '#70757a' }}>
                                            ({formatDuration(cls.fromTime, cls.toTime)})
                                        </span>
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Person sx={{ fontSize: 16, mr: 1, color: '#5f6368' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {cls.tutor?.username || `Tutor ID: ${cls.tutor?.id}`}
                                    </Typography>
                                </Box>

                                {(cls.subject?.name || cls.questionType?.name) && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                        <School sx={{ fontSize: 16, mr: 1, color: '#5f6368' }} />
                                        <Box>
                                            {cls.subject?.name && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {cls.subject.name}
                                                </Typography>
                                            )}
                                            {cls.questionType?.name && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {cls.questionType.name}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Groups sx={{ fontSize: 16, mr: 1, color: '#5f6368' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {cls.totalParticipants || cls.participants?.length || 0} Participants
                                    </Typography>
                                </Box>

                                {cls.description && (
                                    <Accordion 
                                        expanded={expandedClass === cls.id}
                                        onChange={() => setExpandedClass(expandedClass === cls.id ? false : cls.id)}
                                        sx={{ mb: 2, boxShadow: 'none', '&:before': { display: 'none' } }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography variant="caption" color="primary">
                                                View Description
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ pt: 0 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {cls.description}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                <Box sx={{ mt: 'auto' }}>
                                    {isClassLive(cls.fromTime, cls.toTime) && cls.meetingLink && (
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="success"
                                            startIcon={<VideoCall />}
                                            onClick={() => handleJoinClass(cls.meetingLink, cls.title)}
                                            sx={{ mb: 1 }}
                                        >
                                            Join Live Class
                                        </Button>
                                    )}

                                    {cls.meetingLink && !isClassLive(cls.fromTime, cls.toTime) && (
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            startIcon={<Link />}
                                            onClick={() => window.open(cls.meetingLink, '_blank')}
                                            sx={{ mb: 1 }}
                                        >
                                            View Meeting Link
                                        </Button>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            onClick={() => handleEditClass(cls.id)}
                                            sx={{ flex: 1 }}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Delete />}
                                            sx={{ flex: 1 }}
                                        >
                                            Delete
                                        </Button>
                                    </Box>
                                </Box>

                                {(cls.worksheets?.length > 0 || cls.sampleBooklets?.length > 0) && (
                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                                            Resources
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {cls.worksheets?.length > 0 && (
                                                <Chip
                                                    icon={<Assignment />}
                                                    label={`${cls.worksheets.length} Worksheet${cls.worksheets.length > 1 ? 's' : ''}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                            {cls.sampleBooklets?.length > 0 && (
                                                <Chip
                                                    icon={<Description />}
                                                    label={`${cls.sampleBooklets.length} Sample Booklet${cls.sampleBooklets.length > 1 ? 's' : ''}`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default ScheduledCalendarList;
